import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Lightning, Cpu, Target, ChatCircle, ArrowLeft, Rocket, Cube,
  Question, Wrench, ArrowRight, ArrowLeft as ArrowLeftIcon, ListChecks, MagicWand,
  Lock, ShieldCheck
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import DomainScout from "@/components/DomainScout";
import LLCAnchor from "@/components/LLCAnchor";
import ArsenalPanel from "@/components/ArsenalPanel";
import IdentityAnchor from "@/components/IdentityAnchor";
import APIVault from "@/components/APIVault";

const STAGE_ICONS = {
  hull_commissioning: Lightning,
  exoskeleton_outfitting: Cpu,
  signal_ignition: Target,
};

export default function BlueprintView() {
  const { id } = useParams();
  const [bp, setBp] = useState(null);
  const [activeStage, setActiveStage] = useState(null);
  const [mode, setMode] = useState("wizard"); // "wizard" | "list"
  const [wizardStep, setWizardStep] = useState(0);
  const [identityVerified, setIdentityVerified] = useState(false);

  useEffect(() => {
    api.get(`/blueprint/${id}`).then((r) => {
      setBp(r.data);
      if (r.data.blueprint?.stages?.[0]) setActiveStage(r.data.blueprint.stages[0].key);
    }).catch(() => toast.error("Blueprint not found in archive."));
    api.get("/identity/status").then((r) => setIdentityVerified(!!r.data.identity_verified)).catch(() => {});
  }, [id]);

  // Reset wizard step when changing stage
  useEffect(() => { setWizardStep(0); }, [activeStage]);

  const toggle = async (stageKey, idx, checked) => {
    try {
      await api.post("/blueprint/checklist", {
        blueprint_id: id,
        stage_key: stageKey,
        milestone_idx: idx,
        completed: checked,
      });
      setBp({
        ...bp,
        progress: {
          ...bp.progress,
          [stageKey]: { ...(bp.progress?.[stageKey] || {}), [idx]: checked },
        },
      });
    } catch (e) {
      toast.error("Failed to update.");
    }
  };

  if (!bp) {
    return <div className="py-32 text-center text-white/40 overline" data-testid="bp-loading">Decrypting blueprint...</div>;
  }

  const stages = bp.blueprint?.stages || [];
  const progress = bp.progress || {};
  const stageProgress = (stage) => {
    const total = stage.milestones.length;
    const done = Object.values(progress[stage.key] || {}).filter(Boolean).length;
    return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
  };

  const activeStageObj = stages.find((s) => s.key === activeStage);
  const totalMilestones = stages.reduce((sum, s) => sum + s.milestones.length, 0);
  const totalDone = Object.values(progress).reduce((sum, st) => sum + Object.values(st).filter(Boolean).length, 0);
  const overallPct = totalMilestones ? Math.round((totalDone / totalMilestones) * 100) : 0;

  const isHullStage = activeStageObj?.key === "hull_commissioning";
  const isExoskeletonStage = activeStageObj?.key === "exoskeleton_outfitting";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="blueprint-view-page">
      <Link to="/commander" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-[#FFD700] mb-6" data-testid="back-to-deck">
        <ArrowLeftIcon size={14} /> Commander Deck
      </Link>

      {/* Header */}
      <div className="mb-10">
        <span className="overline">// Sovereign Blueprint // {bp.business_type}</span>
        <h1 className="heading-display text-3xl md:text-5xl mt-3 mb-4" data-testid="bp-title">
          {bp.business_type}
        </h1>
        <div className="surface-card p-6 flex items-start gap-4 max-w-3xl">
          <ChatCircle size={22} weight="duotone" className="text-[#FFD700] flex-shrink-0 mt-1" />
          <div>
            <div className="overline text-[0.6rem] mb-1">Director's Brief</div>
            <p className="text-white/80 leading-relaxed">{bp.blueprint?.summary}</p>
          </div>
        </div>

        <div className="mt-6 max-w-md">
          <div className="flex justify-between text-[0.65rem] font-mono uppercase tracking-widest text-white/50 mb-2">
            <span>Mission Progress</span>
            <span className="text-[#FFD700]">{totalDone}/{totalMilestones} • {overallPct}%</span>
          </div>
          <div className="h-1.5 bg-white/10">
            <div className="h-full bg-[#FFD700] transition-all" style={{ width: `${overallPct}%` }} />
          </div>
        </div>
      </div>

      {/* Stage navigation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="stage-nav">
        {stages.map((stage, idx) => {
          const Icon = STAGE_ICONS[stage.key] || Cube;
          const sp = stageProgress(stage);
          const active = activeStage === stage.key;
          const isLocked = !identityVerified && stage.key !== "hull_commissioning";
          return (
            <button
              key={stage.key}
              onClick={() => {
                if (isLocked) {
                  toast.error("Identity Anchor required. Complete Stage 1 verification first.");
                  setActiveStage("hull_commissioning");
                  return;
                }
                setActiveStage(stage.key);
              }}
              data-testid={`stage-tab-${stage.key}`}
              className={`surface-card p-5 text-left transition-all relative ${active ? "stage-active border-[#FFD700]" : ""} ${isLocked ? "opacity-60" : ""}`}
            >
              {isLocked && (
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 border border-[#FFD700]/40">
                  <Lock size={10} weight="bold" className="text-[#FFD700]" />
                  <span className="font-mono text-[0.55rem] uppercase tracking-widest text-[#FFD700]">Locked</span>
                </div>
              )}
              <div className="flex items-start justify-between mb-3">
                <Icon size={26} weight="duotone" className={active ? "text-[#FFD700]" : "text-white/60"} />
                {!isLocked && (
                  <span className="font-mono text-[0.65rem] uppercase tracking-widest text-white/40">
                    {sp.done}/{sp.total}
                  </span>
                )}
              </div>
              <div className="overline text-[0.55rem] mb-1">Stage {idx + 1}</div>
              <div className={`font-bold mb-1 ${active ? "text-white" : "text-white/80"}`}>{stage.title.replace(/^Stage \d+:\s*/, "")}</div>
              <div className="text-xs text-white/50 font-light">{stage.subtitle}</div>
              {!isLocked && (
                <div className="mt-3 h-1 bg-white/10">
                  <div className="h-full bg-[#FFD700] transition-all" style={{ width: `${sp.pct}%` }} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Identity Anchor — required on Hull stage; persistent if not verified */}
      {activeStage === "hull_commissioning" && (
        <div className="mb-8">
          <IdentityAnchor onVerified={() => setIdentityVerified(true)} />
        </div>
      )}

      {/* Mode toggle */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/5">
        <div className="overline text-[0.6rem]">// View Mode</div>
        <div className="flex border border-white/10">
          <button
            data-testid="mode-wizard"
            onClick={() => setMode("wizard")}
            className={`px-4 py-2 text-[0.65rem] uppercase tracking-widest font-semibold flex items-center gap-2 transition-all ${
              mode === "wizard" ? "bg-[#FFD700] text-black" : "text-white/60 hover:text-white"
            }`}
          >
            <MagicWand size={12} /> Hand-Walk
          </button>
          <button
            data-testid="mode-list"
            onClick={() => setMode("list")}
            className={`px-4 py-2 text-[0.65rem] uppercase tracking-widest font-semibold flex items-center gap-2 transition-all ${
              mode === "list" ? "bg-[#FFD700] text-black" : "text-white/60 hover:text-white"
            }`}
          >
            <ListChecks size={12} /> Checklist
          </button>
        </div>
      </div>

      {/* Active stage content */}
      {activeStageObj && (
        <motion.div
          key={activeStageObj.key + mode}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid lg:grid-cols-3 gap-8"
          data-testid={`stage-active-${activeStageObj.key}`}
        >
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Director's note */}
            <div className="surface-card p-6 flex items-start gap-4">
              <ChatCircle size={22} weight="duotone" className="text-[#FFD700] flex-shrink-0 mt-1" />
              <div>
                <div className="overline text-[0.6rem] mb-2">Director's Note</div>
                <p className="text-white/80 italic">"{activeStageObj.director_note}"</p>
              </div>
            </div>

            {/* WIZARD MODE */}
            {mode === "wizard" && (
              <WizardView
                stage={activeStageObj}
                step={wizardStep}
                setStep={setWizardStep}
                progress={progress[activeStageObj.key] || {}}
                onToggle={(idx, checked) => toggle(activeStageObj.key, idx, checked)}
              />
            )}

            {/* LIST/CHECKLIST MODE */}
            {mode === "list" && (
              <div className="surface-card p-8">
                <h3 className="overline mb-6">// {activeStageObj.subtitle} // Interactive Milestones</h3>
                <div className="space-y-3">
                  {activeStageObj.milestones.map((m, i) => {
                    const checked = !!progress[activeStageObj.key]?.[i];
                    return (
                      <div
                        key={i}
                        className={`flex items-start gap-4 p-4 border transition-all ${
                          checked ? "border-[#FFD700]/30 bg-[#FFD700]/5" : "border-white/5 hover:border-white/15"
                        }`}
                        data-testid={`milestone-${activeStageObj.key}-${i}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => toggle(activeStageObj.key, i, !!v)}
                          className="mt-1 border-white/30 data-[state=checked]:bg-[#FFD700] data-[state=checked]:border-[#FFD700] data-[state=checked]:text-black"
                          data-testid={`checkbox-${activeStageObj.key}-${i}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`font-semibold mb-1 ${checked ? "text-white/50 line-through" : "text-white"}`}>
                            {m.task}
                          </div>
                          {m.why && <p className="text-sm text-white/70 font-light mb-1"><span className="overline text-[0.55rem] text-[#FFD700]/70 mr-1">WHY ›</span> {m.why}</p>}
                          {m.how && <p className="text-sm text-white/60 font-light"><span className="overline text-[0.55rem] text-[#FFD700]/70 mr-1">HOW ›</span> {m.how}</p>}
                          {!m.why && !m.how && m.detail && <p className="text-sm text-white/60 font-light">{m.detail}</p>}
                          {m.estimate && (
                            <div className="mt-2 font-mono text-[0.6rem] uppercase tracking-widest text-[#FFD700]/80">
                              ⏱ {m.estimate}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Infrastructure tools for Hull stage */}
            {isHullStage && (
              <>
                <DomainScout />
                <LLCAnchor />
              </>
            )}

            {/* Sovereign API Vault — primary tool for Exoskeleton Outfitting */}
            {isExoskeletonStage && (
              <APIVault />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Ghost Crew */}
            {bp.blueprint?.ghost_crew && bp.blueprint.ghost_crew.length > 0 && (
              <div className="surface-card p-6" data-testid="ghost-crew-panel">
                <div className="flex items-center gap-3 mb-4">
                  <Cpu size={20} weight="duotone" className="text-[#FFD700]" />
                  <h3 className="font-bold uppercase tracking-wider text-sm">Your Ghost Crew</h3>
                </div>
                <div className="space-y-3">
                  {bp.blueprint.ghost_crew.map((g, i) => (
                    <div key={i} className="border-l-2 border-[#FFD700]/30 pl-3">
                      <div className="font-semibold text-sm text-white">{g.agent}</div>
                      <div className="text-xs text-white/60 font-light">{g.purpose}</div>
                      {g.tool && <div className="font-mono text-[0.6rem] uppercase tracking-widest text-[#FFD700]/70 mt-1">{g.tool}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Niche Arsenal */}
            <ArsenalPanel arsenal={bp.blueprint?.arsenal} />

            {/* First Strike */}
            {bp.blueprint?.first_strike && (
              <div className="surface-card armor-border p-6" data-testid="first-strike-panel">
                <div className="flex items-center gap-3 mb-3">
                  <Rocket size={20} weight="duotone" className="text-[#FFD700]" />
                  <h3 className="font-bold uppercase tracking-wider text-sm">48-Hour Strike</h3>
                </div>
                <p className="text-sm text-white/80 font-light leading-relaxed">{bp.blueprint.first_strike}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ====== WIZARD VIEW ======
function WizardView({ stage, step, setStep, progress, onToggle }) {
  const milestones = stage.milestones || [];
  const m = milestones[step];
  if (!m) return null;

  const checked = !!progress[step];
  const total = milestones.length;
  const isLast = step === total - 1;
  const isFirst = step === 0;

  return (
    <motion.div
      key={`wiz-${stage.key}-${step}`}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="surface-card p-8 md:p-10"
      data-testid={`wizard-step-${stage.key}-${step}`}
    >
      {/* Step indicator */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[0.65rem] uppercase tracking-widest text-white/50">
            Step {step + 1} of {total}
          </span>
        </div>
        <div className="flex gap-1">
          {milestones.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              data-testid={`wizard-jump-${i}`}
              className={`h-1 w-6 transition-all ${
                i === step ? "bg-[#FFD700]" : progress[i] ? "bg-[#FFD700]/50" : "bg-white/15 hover:bg-white/30"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Task */}
      <div className="mb-6">
        <div className="overline text-[0.6rem] mb-2">// Mission Objective</div>
        <h3 className="heading-display text-2xl md:text-3xl text-white mb-3">{m.task}</h3>
        {m.estimate && (
          <div className="font-mono text-xs uppercase tracking-widest text-[#FFD700]/80">⏱ {m.estimate}</div>
        )}
      </div>

      {/* Why */}
      {m.why && (
        <div className="mb-5 border-l-2 border-[#FFD700] pl-5 py-2">
          <div className="flex items-center gap-2 mb-2">
            <Question size={16} weight="duotone" className="text-[#FFD700]" />
            <span className="overline text-[0.6rem]">The Why</span>
          </div>
          <p className="text-base text-white/90 font-light leading-relaxed">{m.why}</p>
        </div>
      )}

      {/* How */}
      {m.how && (
        <div className="mb-6 border-l-2 border-[#FFD700]/50 pl-5 py-2">
          <div className="flex items-center gap-2 mb-2">
            <Wrench size={16} weight="duotone" className="text-[#FFD700]" />
            <span className="overline text-[0.6rem]">The How</span>
          </div>
          <p className="text-base text-white/80 font-light leading-relaxed">{m.how}</p>
        </div>
      )}

      {!m.why && !m.how && m.detail && (
        <p className="mb-6 text-base text-white/70 font-light leading-relaxed">{m.detail}</p>
      )}

      {/* Complete checkbox + navigation */}
      <div className="flex items-center justify-between pt-6 mt-6 border-t border-white/5 gap-4 flex-wrap">
        <label className="flex items-center gap-3 cursor-pointer" data-testid={`wizard-complete-${stage.key}-${step}`}>
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => onToggle(step, !!v)}
            className="border-white/30 data-[state=checked]:bg-[#FFD700] data-[state=checked]:border-[#FFD700] data-[state=checked]:text-black"
          />
          <span className={`text-sm uppercase tracking-wider font-semibold ${checked ? "text-[#FFD700]" : "text-white/70"}`}>
            {checked ? "Objective Complete" : "Mark as Complete"}
          </span>
        </label>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={isFirst}
            data-testid="wizard-prev-btn"
            className="btn-ghost px-5 py-3 text-xs flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon size={14} /> Prev
          </button>
          {!isLast ? (
            <button
              onClick={() => setStep(step + 1)}
              data-testid="wizard-next-btn"
              className="btn-gold px-5 py-3 text-xs flex items-center gap-2"
            >
              Next Objective <ArrowRight size={14} />
            </button>
          ) : (
            <div className="text-[0.65rem] font-mono uppercase tracking-widest text-[#FFD700]">
              Stage Complete ✓
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
